/**
 * CourseDetailPage — course overview with ordered module list.
 *
 * Route: /app/learn/:enrollmentId
 *
 * EPIC-08-04 Acceptance Criteria:
 * - AC-5: Shows course description, objectives, module list
 * - AC-6: Roleplay module → navigates to /session/:id with module_id context
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { ModuleList } from '../components/ModuleList';
import type { CourseModule } from '../components/ModuleList';
import type { ModuleType } from '../hooks/useEnrollments';
import styles from './CourseDetailPage.module.css';

interface CourseDetail {
  id: string;
  title: string;
  description: string;
  language: string;
  objectives: string[];
  modules: CourseModule[];
  enrollment_status: string;
  score: number | null;
}

export function CourseDetailPage(): JSX.Element {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const { t } = useTranslation('training');
  const navigate = useNavigate();

  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enrollmentId) return;
    let cancelled = false;

    async function load(): Promise<void> {
      try {
        const res = await apiClient.get<{ data: CourseDetail }>(
          `/enrollments/${enrollmentId}/course`,
        );
        if (!cancelled) setCourse(res.data.data);
      } catch {
        if (!cancelled) setError('load_failed');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [enrollmentId]);

  const [retaking, setRetaking] = useState(false);

  async function handleRetake(): Promise<void> {
    if (!enrollmentId || retaking) return;
    setRetaking(true);
    try {
      await apiClient.post(`/enrollments/${enrollmentId}/retake`);
      setIsLoading(true);
      const res = await apiClient.get<{ data: CourseDetail }>(
        `/enrollments/${enrollmentId}/course`,
      );
      setCourse(res.data.data);
    } catch {
      alert('Failed to reset course. Please try again.');
    } finally {
      setIsLoading(false);
      setRetaking(false);
    }
  }

  function handleStartModule(
    moduleId: string,
    moduleType: ModuleType,
    aiTutorEnabled?: boolean,
  ): void {
    if (aiTutorEnabled && course) {
      // EPIC-08-11: route ai_tutor_enabled modules to AiTutorPage
      navigate(`/tutor/${course.id}/${moduleId}?enrollmentId=${enrollmentId ?? ''}`);
      return;
    }
    if (moduleType === 'roleplay') {
      if (!course) return;
      sessionStorage.setItem('module_id', moduleId);
      sessionStorage.setItem('enrollment_id', enrollmentId ?? '');
      sessionStorage.setItem('course_feedback_course_id', course.id);
      sessionStorage.setItem('course_feedback_enrollment_id', enrollmentId ?? '');
      sessionStorage.setItem('course_feedback_course_title', course.title);
      sessionStorage.setItem('evaluation_mode', 'assessment');
      navigate('/scenarios');
    } else if (moduleType === 'quiz') {
      navigate(`/quiz/${moduleId}?enrollmentId=${enrollmentId ?? ''}`);
    } else if (moduleType === 'reading') {
      navigate(`/reading/${moduleId}?enrollmentId=${enrollmentId ?? ''}`);
    }
  }

  if (isLoading) {
    return (
      <main className={styles.page}>
        <p className={styles.loading}>{t('common.loading')}</p>
      </main>
    );
  }

  if (error || !course) {
    return (
      <main className={styles.page}>
        <button className={styles.backLink} onClick={() => navigate('/learn')}>
          ← {t('common.button.back')}
        </button>
        <p className={styles.error}>{t('common.error.generic')}</p>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <button className={styles.backLink} onClick={() => navigate('/learn')}>
        ← {t('common.button.back')}
      </button>

      <header className={styles.header}>
        <h1 className={styles.title}>{course.title}</h1>
        <span className={styles.languageTag}>{course.language}</span>
      </header>

      {course.enrollment_status && course.enrollment_status !== 'not_started' && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#eef2ff', borderLeft: '4px solid #4f46e5', borderRadius: '4px' }}>
          <strong>Status: </strong> 
          <span style={{ textTransform: 'capitalize', color: course.enrollment_status === 'completed' ? '#16a34a' : course.enrollment_status === 'failed' ? '#dc2626' : '#2563eb' }}>
            {course.enrollment_status}
          </span>
          {course.score !== null && (
            <span style={{ marginLeft: '1.5rem' }}>
              <strong>Score: </strong> {Math.round(course.score)}%
            </span>
          )}
          {course.enrollment_status === 'failed' && (
            <button
              onClick={() => void handleRetake()}
              disabled={retaking}
              style={{
                marginLeft: '1.5rem',
                padding: '0.25rem 0.75rem',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
              }}
            >
              {retaking ? t('common.loading') : 'Retake Course'}
            </button>
          )}
        </div>
      )}

      <p className={styles.description}>{course.description}</p>

      {course.objectives.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('learn.objectivesTitle')}</h2>
          <ul className={styles.objectivesList}>
            {course.objectives.map((obj, i) => (
              <li key={i}>{obj}</li>
            ))}
          </ul>
        </section>
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('learn.modulesTitle')}</h2>
        <ModuleList modules={course.modules} onStart={handleStartModule} />
      </section>
    </main>
  );
}
